<template>
    <a-card :bordered="false">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <template v-if="advanced">
              <a-col :md="8" :sm="24">
                <a-form-item :label="$t('views.printMultiplesOption.search.multipleNum')" prop="multipleNum">
                  <a-input v-model="queryParam.multipleNum" :placeholder="$t('views.printMultiplesOption.search.multipleNumPlaceholder')" allow-clear/>
                </a-form-item>
              </a-col>
              <a-col :md="8" :sm="24">
                <a-form-item :label="$t('views.printMultiplesOption.search.sort')" prop="sort">
                  <a-input v-model="queryParam.sort" :placeholder="$t('views.printMultiplesOption.search.sortPlaceholder')" allow-clear/>
                </a-form-item>
              </a-col>
            </template>
            <a-col :md="!advanced && 8 || 24" :sm="24">
              <span class="table-page-search-submitButtons" :style="advanced && { float: 'right', overflow: 'hidden' } || {} ">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ $t('views.printMultiplesOption.btn.query') }}</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <div class="table-operations">
        <a-button type="primary" @click="$refs.createForm.handleAdd()" v-hasPermi="['user:printMultiplesOption:add']">
          <a-icon type="plus" />{{ $t('views.printMultiplesOption.btn.add') }}
        </a-button>
        <a-button type="primary" :disabled="single" @click="$refs.createForm.handleUpdate(undefined, ids)" v-hasPermi="['user:printMultiplesOption:edit']">
          <a-icon type="edit" />{{ $t('views.printMultiplesOption.btn.edit') }}
        </a-button>
        <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['user:printMultiplesOption:remove']">
          <a-icon type="delete" />{{ $t('views.printMultiplesOption.btn.delete') }}
        </a-button>
      </div>
      <create-form
        ref="createForm"
        :default-print-multiples-id="effectiveFixedPrintMultiplesId"
        @ok="getList"
      />
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered"
        :scroll="{ x: 'max-content' }"
      >
        <span slot="sort" slot-scope="text, record">
          <a-input-number
            :value="record.sort"
            :min="0"
            :disabled="!canEdit"
            style="width: 100px"
            @change="(value) => handleSortChange(record, value, 'sort')"
          />
        </span>
        <span slot="status" slot-scope="text, record">
          <a-switch
            :checked="record.status == 1 || record.status == '1'"
            :disabled="!canEdit"
            @change="(checked) => handleStatusChange(record, checked)"
          />
        </span>
        <span slot="createTime" slot-scope="text, record">
          {{ parseTime(record.createTime) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a-divider type="vertical" v-hasPermi="['user:printMultiplesOption:edit']" />
          <a @click="$refs.createForm.handleUpdate(record, undefined)" v-hasPermi="['user:printMultiplesOption:edit']">
            <a-icon type="edit" />{{ $t('views.printMultiplesOption.btn.edit') }}
          </a>
          <a-divider type="vertical" v-hasPermi="['user:printMultiplesOption:remove']" />
          <a @click="handleDelete(record)" v-hasPermi="['user:printMultiplesOption:remove']">
            <a-icon type="delete" />{{ $t('views.printMultiplesOption.btn.delete') }}
          </a>
        </span>
      </a-table>
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="paginationShowTotal"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
</template>

<script>
import { pagePrintMultiplesOption, delPrintMultiplesOption, updatePrintMultiplesOption } from '@/api/user/printMultiplesOption'
import CreateForm from './modules/CreateForm'
import { tableMixin } from '@/store/table-mixin'
import auth from '@/plugins/auth'

export default {
  name: 'PrintMultiplesOption',
  components: {
    CreateForm
  },
  mixins: [tableMixin],
  props: {
    fixedPrintMultiplesId: {
      type: [Number, String],
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      advanced: false,
      single: true,
      multiple: true,
      ids: [],
      loading: false,
      total: 0,
      queryParam: {
        printMultiplesId: null,
        metersNum: null,
        multipleNum: null,
        sort: null,
        status: null,
        pageNum: 1,
        pageSize: 10
      },
      columns: []
    }
  },
  created () {
    this.buildColumns()
  },
  watch: {
    '$i18n.locale' () {
      this.buildColumns()
    },
    fixedPrintMultiplesId: {
      handler () {
        this.syncFixedPrintMultiplesFilter()
      },
      immediate: true
    }
  },
  computed: {
    effectiveFixedPrintMultiplesId () {
      const v = this.fixedPrintMultiplesId
      if (v === null || v === undefined || v === '') {
        return null
      }
      return v
    },
    isPrintMultiplesFilterLocked () {
      return this.effectiveFixedPrintMultiplesId != null && this.effectiveFixedPrintMultiplesId !== ''
    },
    /** 与编辑接口一致：行内排序/状态走 edit */
    canEdit () {
      return auth.hasPermi('user:printMultiplesOption:edit')
    }
  },
  methods: {
    buildColumns () {
      const t = k => this.$t(k)
      this.columns = [
        { title: t('views.printMultiplesOption.column.id'), dataIndex: 'id', ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.metersNum'), dataIndex: 'metersNum', ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.multipleNum'), dataIndex: 'multipleNum', ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.sort'), dataIndex: 'sort', scopedSlots: { customRender: 'sort' }, ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.status'), dataIndex: 'status', scopedSlots: { customRender: 'status' }, ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.createTime'), dataIndex: 'createTime', scopedSlots: { customRender: 'createTime' }, ellipsis: true, align: 'center' },
        { title: t('views.printMultiplesOption.column.operation'), dataIndex: 'operation', width: 200, fixed: 'right', scopedSlots: { customRender: 'operation' }, align: 'center' }
      ]
    },
    paginationShowTotal (total) {
      return this.$t('views.printMultiplesOption.pagination.total', { total })
    },
    syncFixedPrintMultiplesFilter () {
      const fixed = this.effectiveFixedPrintMultiplesId
      if (fixed != null && fixed !== '') {
        this.queryParam.printMultiplesId = fixed
      } else if (this.embedded) {
        this.queryParam.printMultiplesId = null
      }
      this.queryParam.pageNum = 1
      this.getList()
    },
    getList () {
      this.loading = true
     pagePrintMultiplesOption(this.queryParam).then(response => {
        this.list = response.data.records
        this.total = response.data.total
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      const fixed = this.effectiveFixedPrintMultiplesId
      this.queryParam = {
        printMultiplesId: fixed != null && fixed !== '' ? fixed : undefined,
        metersNum: undefined,
        multipleNum: undefined,
        sort: undefined,
        status: undefined,
        pageNum: 1,
        pageSize: 10
      }
      this.handleQuery()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.id)
      this.single = selectedRowKeys.length !== 1
      this.multiple = !selectedRowKeys.length
    },
    toggleAdvanced () {
      this.advanced = !this.advanced
    },
    handleDelete (row) {
      var that = this
      const ids = row.id || this.ids
      if(row.id){
        this.ids.push(row.id)
      }
      this.$confirm({
        title: this.$t('views.printMultiplesOption.confirm.deleteTitle'),
        content: this.$t('views.printMultiplesOption.confirm.deleteContent', { ids: ids }),
        onOk () {
          let data = []
          that.ids.map(id => data.push({"id":id}))
          return delPrintMultiplesOption(data)
            .then(() => {
              that.onSelectChange([], [])
              that.getList()
              that.$message.success(that.$t('views.printMultiplesOption.msg.deleteSuccess'), 3)
          })
        },
        onCancel () {
          that.ids = []
        }
      })
    },
    handleExport () {
      var that = this
      this.$confirm({
        title: this.$t('views.printMultiplesOption.confirm.exportTitle'),
        content: this.$t('views.printMultiplesOption.confirm.exportContent'),
        onOk () {
          that.download('user/print-multiples-option/export', {
            ...that.queryParam
          }, that.$t('views.printMultiplesOption.export.filename', { time: new Date().getTime() }))
        },
        onCancel () {}
      })
    },
    handleStatusChange (record, checked) {
      if (!this.canEdit) {
        return
      }
      const statusValue = checked ? 1 : 0
      const updateData = {
        id: record.id,
        status: statusValue
      }
      updatePrintMultiplesOption(updateData).then(() => {
        record.status = statusValue
        this.$message.success(this.$t('views.printMultiplesOption.msg.statusOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.printMultiplesOption.msg.statusFail'))
      })
    },
    handleSortChange (record, value, fieldName) {
      if (!this.canEdit) {
        return
      }
      if (value === null || value === undefined) {
        return
      }
      const updateData = {
        id: record.id
      }
      updateData[fieldName] = value
      updatePrintMultiplesOption(updateData).then(() => {
        record[fieldName] = value
        this.$message.success(this.$t('views.printMultiplesOption.msg.sortOk'))
        this.getList()
      }).catch(() => {
        this.$message.error(this.$t('views.printMultiplesOption.msg.sortFail'))
      })
    }
  }
}
</script>

<style scoped>
.print-multiples-option--embedded ::v-deep .ant-page-header-heading {
  display: none;
}
.print-multiples-option--embedded ::v-deep .ant-pro-page-header-wrap-children-content {
  margin: 0;
}
</style>
